import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import Button from 'react-bootstrap/lib/Button';
import { Map } from 'immutable';

import urls from '../../urls';
import OrderActions from '../../actions/OrderActions';
import OrderStore from '../../stores/OrderStore';
import { translate as t } from '../../lib/I18n';
import TripsStore from '../../stores/TripsStore';
import CurrentTripStore from '../../stores/CurrentTripStore';
import UserStore from '../../stores/UserStore';
import SmartcardStore from '../../stores/SmartcardStore';
import PaymentStore from '../../stores/PaymentStore';
import ModalActions from '../../actions/ModalActions';
import { GlobalModals, CheckoutStages, OrderSummaryContexts, PointStages } from '../../constants/AppConstants';
import connectToStores from '../../helpers/connectToStores';
import Trip from '../../records/Trip';
import Order from '../../records/Order';
import Smartcard from '../../records/customer/Smartcard';

import Icon from '../general/Icon';
import BaseModal from '../general/BaseModal';
import ConfirmModal from '../general/ConfirmModal';
import OrderSummary from '../checkout/OrderSummary';
import PureRenderComponent from '../PureRenderComponent';
import TrackingActions from '../../actions/TrackingActions';

function _stateFromStores () {
    return {
        currentOrder: OrderStore.getCurrent(),
        trips: TripsStore.getAll(),
        trip: CurrentTripStore.get(),
        smartcard: SmartcardStore.getCurrent(),
        isProcessing: PaymentStore.isProcessing(),
        is3DSecure: PaymentStore.is3DSecure(),
        isLoggedIn: UserStore.getCurrent().isLoggedIn()
    };
}

@connectToStores([ OrderStore, TripsStore, SmartcardStore, CurrentTripStore, PaymentStore, UserStore ], _stateFromStores)
class BasketModal extends PureRenderComponent {

    static propTypes = {
        router: PropTypes.any.isRequired,
        currentOrder: PropTypes.instanceOf(Order).isRequired,
        trips: PropTypes.instanceOf(Map).isRequired,
        trip: PropTypes.instanceOf(Trip).isRequired,
        smartcard: PropTypes.instanceOf(Smartcard),
        onCancel: PropTypes.func,
        isProcessing: PropTypes.bool,
        is3DSecure: PropTypes.bool,
        isLoggedIn: PropTypes.bool
    };

    static defaultProps = {
        onCancel: _.noop,
        isProcessing: false,
        is3DSecure: false,
        isLoggedIn: false
    };

    state = {
        modalOpen: false,
        basketChanged: false,
    };

    _clearBasket = () => {
        OrderActions.clearBasket(this.props.currentOrder);
        this._toggleModal();
        this._handleClose();
    };

    _toggleModal = () => {
        this.setState({ modalOpen: !this.state.modalOpen });
    };

    _handleClose = () => {
        ModalActions.close(GlobalModals.BASKET);
        if (this.state.basketChanged) {
            this.props.router.push(urls.location(CheckoutStages.DELIVERY));
        }
        this.props.onCancel();
    };

    _handleCheckout = () => {
        this.props.router.push(urls.location(CheckoutStages.DELIVERY));
        this._handleClose();
    };

    _renderConfirmClear = () => {
        const { isProcessing, is3DSecure } = this.props;
        const isLocked = isProcessing || is3DSecure;

        return (
            <ConfirmModal
                titleText={ t('booking.summary.emptyBasket') }
                show={ this.state.modalOpen }
                text= { t('booking.summary.confirmClear') }
                className="modal--sm"
                continueText="button.text.confirm"
                closeText="button.text.cancel"
                disableContinue={ isLocked }
                onClose={ this._toggleModal }
                onContinue={ this._clearBasket }
            />
        );
    };

    _renderTripError ({ trips, trip, currentOrder }) {
        const test = trips.first();
        const isSmartcardOrder = !!(test && test.isSmartcard());

        if (test && trip.isSmartcard() !== isSmartcardOrder) {
            return (
                <div className="box--padded margin-top--sm">
                    <div className="confirmation text-center">
                        <Icon icon="error margin-bottom--sm otrl-xl" />
                        <p className="alert-warning no-margin text-weight--normal">{ t('basket.error.smartcardTicketConflict') }</p>
                    </div>
                </div>
            );
        }

        if (!currentOrder.isEmpty() && trip.isSmartcard()) {
            return (
                <div className="box--padded margin-top--sm">
                    <div className="confirmation text-center">
                        <Icon icon="error margin-bottom--sm otrl-xl" />
                        <p className="alert-warning no-margin text-weight--normal">{ t('basket.error.tooManyTrips') }</p>
                    </div>
                </div>
            );
        }

        return null;
    }

    goBackToJourneyPlanner = () => {
        TrackingActions.addAnotherJourneyOnBasketModal("add-another-journey");
        ModalActions.close(GlobalModals.BASKET);
        this.props.router.push(PointStages.SEARCH);
    };

    _renderEmptyButton ({ currentOrder }) {
        if (currentOrder.isEmpty()) { return null; }
        return (
            <span className="multiple-buttons">
                <Button className="action-btn action-btn--secondary" onClick={ this._toggleModal }>
                    { t('booking.summary.emptyBasket') }
                </Button>
                <Button className="action-btn action-btn--secondary" onClick={ this.goBackToJourneyPlanner }>
                    { t('booking.summary.addAnotherJourney') }
                </Button>
            </span>
        );
    }

    render () {
        const { currentOrder, isProcessing, is3DSecure, isLoggedIn } = this.props;
        const isLocked = currentOrder.isEmpty() || isProcessing || is3DSecure;

        const setBasketChanged = () => this.setState({ basketChanged: true });

        return (
            <BaseModal
                    titleText="booking.summary.modalTitle"
                    className="modal--basket"
                    onClose={ this._handleClose }
                    onContinue={ this._handleCheckout }
                    continueText="button.text.checkout"
                    closeText="button.text.close"
                    disableContinue={ isLocked }
                    footerContent={ this._renderEmptyButton(this.props) }
                    onEnterContinue={ false } >
                { this._renderTripError(this.props) }
                { this._renderConfirmClear() }
                <OrderSummary
                    order={ currentOrder }
                    showHeader={ false }
                    isLocked={ isLocked }
                    isLoggedIn={ isLoggedIn }
                    context={ OrderSummaryContexts.BASKET_MODAL }
                    setBasketChanged={setBasketChanged}
                />
            </BaseModal>
        );
    }
}

export default BasketModal;
